import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ForgotPasswordComponent } from './pages/reset-password/forgot-password.component';
import {MaterialModule} from "./material/material.module";
import {LoginComponent} from "./pages/login/login.component";
import {RegisterComponent} from "./pages/register/register.component";
import {ProfileComponent} from "./pages/profile/profile.component";
import {UserDashboardComponent} from "./pages/dashboard/user-dashboard.component";
import {DashboardComponent} from "./pages/dashboard/dashboard.component";
import {BillingComponent} from "./pages/billing/billing.component";
import {VerifyComponent} from "./pages/verify/verify.component";
import {LandingComponent} from "./pages/landing/landing.component";
import {JwtModule} from "@auth0/angular-jwt";
import {Auth} from "./auth/auth.service";
import {CustomHttpInterceptor} from "./_interceptors/http.interceptor";
import {MyWalletComponent} from "./pages/my-wallet/my-wallet.component";
import {SubscriptionCheckComponent} from "./pages/subscription-check/subscription-check.component";
import {DashboardLayout} from "./layout/dashboard.layout";
import { NgxMaskModule, IConfig } from 'ngx-mask';
import {MainLayout} from "./layout/main.layout";
import {SidenavComponent} from "./layout/sidenav.component";
import {LayoutModule} from "@angular/cdk/layout";
import {NewUserModal} from "./pages/dashboard/modals/new-user.modal";
import {BoardAdminComponent} from "./pages/admin-board/admin-board.component";
import {AlertComponent} from "./_shared/alert/alert.component";
import {CeuListComponent} from "./pages/ceu-list/ceu-list.component";
import {BrandambassadorComponent} from "./pages/brand-ambassador/brandambassador.component";
import {EmailAgentComponent} from "./pages/email-agent/email-agent.component";
import {UpgradePlanComponent} from "./pages/upgrade-plan/upgrade-plan.component";
import {MatTooltipModule} from "@angular/material/tooltip";
import {PaginationComponent} from "./_shared/pagination/pagination.component";
import {UpcomingCeuAdvanceSearchDialog} from "./pages/dashboard/modals/upcoming-ceu-advance-search.dialog";
import {CertificationListComponent} from "./pages/my-wallet/certification-list.component";
import {WalletCeuListComponent} from "./pages/my-wallet/wallet-ceu-list.component";
import {AddUserCertificationModal} from "./pages/my-wallet/modals/add-user-certification.modal";
import {UploadCeuCreditModal} from "./pages/my-wallet/modals/upload-ceu-credit.modal";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from "@angular/material-moment-adapter";
import {PICKER_DATE_FORMATS} from "./_constants/common.constant";
import {ConfirmationDialog} from "./_shared/dialog/confirmation.dialog";
import {InformationDialog} from "./_shared/dialog/information.dialog";
import {AppUploadSingleComponent} from "./_shared/upload-single/upload-single.component";
import {ErrorInterceptor} from "./_interceptors/error.interceptor";
import {ConversationComponent} from "./pages/email-agent/conversation.component";
import {SupportInboxComponent} from "./pages/admin-board/support-inbox.component";
import {AdminConversationComponent} from "./pages/admin-board/admin-conversation.component";
import {NgProgressHttpModule} from "ngx-progressbar/http";
import {NgProgressModule} from "ngx-progressbar";
import {ComingSoonComponent} from "./pages/coming-soon/coming-soon.component";
import {ResetPasswordComponent} from "./pages/reset-password/reset-password.component";
import {UserManagementComponent} from "./pages/admin-board/user-management.component";
import {UserManagementDetailComponent} from "./pages/admin-board/user-management-detail.component";
import {NotificationModal} from "./pages/dashboard/modals/notification.modal";
import {UserManCeuWalletComponent} from "./pages/admin-board/user-man-ceu-wallet.component";
import {UserManCertWalletComponent} from "./pages/admin-board/user-man-cert-wallet.component";
import {AdminUserAdvanceSearchDialog} from "./pages/admin-board/modals/admin-user-advance-search.dialog";
import {AdminCertificationComponent} from "./pages/admin-board/admin-certification.component";
import {AdminCeuComponent} from "./pages/admin-board/admin-ceu.component";
import {AdminAddNewTrainingDialog} from "./pages/admin-board/modals/admin-add-new-training.dialog";
import {AdminAddNewCertificationDialog} from "./pages/admin-board/modals/admin-add-new-certification.dialog";
import {AdminUserDialog} from "./pages/admin-board/modals/admin-user.dialog";
import {AssessmentComponent} from "./pages/admin-board/assessment.component";
import {UserManAssessmentComponent} from "./pages/admin-board/user-man-assessment.component";
import {UserManAssessmentEditorComponent} from "./pages/admin-board/user-man-assessment-editor.component";
import { MyAssessmentComponent} from "./pages/my-wallet/my-assessment.component";
import {MyAssessmentViewComponent} from "./pages/my-wallet/my-assessment-view.component";
import {AddProviderDialog} from "./pages/admin-board/modals/add-provider.dialog";
import {NgOptimizedImage} from "@angular/common";
import {AdminDarkLayout} from "./layout/admin-dark.layout";
import {AssociationListComponent} from "./pages/associactions/association-list.component";
import {OfficeBoardComponent} from "./pages/office-board/office-board.component";
import {AssociationFormComponent} from "./pages/associactions/association-form.component";
import {AssociationDetailComponent} from "./pages/associactions/association-detail.component";
import {AssociationEditComponent} from "./pages/associactions/association-edit.component";
import {AssociationActivityListComponent} from "./pages/associactions/association-activity-list.component";
import {AssociationActivityFormComponent} from "./pages/associactions/association-activity-form.component";
import {AssociationActivityDetailComponent} from "./pages/associactions/association-activity-detail.component";
import {PaginationDarkComponent} from "./_shared/pagination-dark/pagination-dark.component";
import {TermDialog} from "./pages/new-certifications/modals/term.dialog";
import {NewCertificationComponent} from "./pages/new-certifications/new-certification.component";
import {NewCertificationFormComponent} from "./pages/new-certifications/new-certification-form.component";
import {NewCertificationDetailComponent} from "./pages/new-certifications/new-certification-detail.component";
import {NewCertDashboardComponent} from "./pages/new-certifications/new-cert-dashboard.component";
import {CertificationConditionCheckComponent} from "./pages/new-certifications/certification-condition-check.component";
import {TiptapComponent} from "./_shared/wysiwyg/tiptap.component";
import {NewCertificationEditComponent} from "./pages/new-certifications/new-certification-edit.component";
import {NewCertificationDomainListComponent} from "./pages/new-certifications/new-certification-domain-list.component";
import {NewCertificationDomainEditComponent} from "./pages/new-certifications/new-certification-domain-edit.component";
import {NewCertificationDomainViewComponent} from "./pages/new-certifications/new-certification-domain-view.component";
import {NewCertificationDomainFormComponent} from "./pages/new-certifications/new-certification-domain-form.component";
import {
    NewCertificationObjectiveListComponent
} from "./pages/new-certifications/new-certification-objective-list.component";
import {
    NewCertificationObjectiveEditComponent
} from "./pages/new-certifications/new-certification-objective-edit.component";
import {
    NewCertificationObjectiveFormComponent
} from "./pages/new-certifications/new-certification-objective-form.component";
import {ViewObjectiveDialog} from "./pages/new-certifications/modals/view-objective.dialog";
import {AdminAddUserCertificationModal} from "./pages/admin-board/modals/add-user-certification.modal";
import {AdminUploadCeuCreditModal} from "./pages/admin-board/modals/upload-ceu-credit.modal";
import {AdminReportComponent} from "./pages/report/admin-report.component";
import {AdminSettingComponent} from "./pages/setting/admin-setting.component";
import {ProviderCertificationListComponent} from "./pages/associactions/provider-certification-list.component";
import {ValidityCheckComponent} from "./_shared/validity-check/validity-check.component";
import {UserManCeuDetailComponent} from "./pages/admin-board/user-man-ceu-detail.component";
import {ValidityCheckLightComponent} from "./_shared/validity-check/validity-check-light.component";
import {WalletCeuDetailComponent} from "./pages/my-wallet/wallet-ceu-detail.component";
import {EscapeHtmlPipe} from "./_shared/pipe/escape-html.pipe";
import {StepperCeuComponent} from "./pages/my-wallet/stepper-ceu.component";
export function tokenGetter(): string {
  return localStorage.getItem('token');
}

const maskConfig: Partial<IConfig> = {
  validation: false,
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    ProfileComponent,
    BoardAdminComponent,
    UserDashboardComponent,
    LandingComponent,
    MyWalletComponent,
    ForgotPasswordComponent,
    AlertComponent,
    DashboardComponent,
    CeuListComponent,
    BillingComponent,
    BrandambassadorComponent,
    EmailAgentComponent,
    VerifyComponent,
    SubscriptionCheckComponent,
    UpgradePlanComponent,
    DashboardLayout,
    MainLayout,
    AdminDarkLayout,
    SidenavComponent,
    NewUserModal,
    PaginationComponent,
    PaginationDarkComponent,
    UpcomingCeuAdvanceSearchDialog,
    CertificationListComponent,
    WalletCeuListComponent,
    AddUserCertificationModal,
    UploadCeuCreditModal,
    ConfirmationDialog,
    InformationDialog,
    AppUploadSingleComponent,
    ConversationComponent,
    SupportInboxComponent,
    AdminConversationComponent,
    ComingSoonComponent,
    ResetPasswordComponent,
    UserManagementComponent,
    UserManagementDetailComponent,
    NotificationModal,
    UserManCeuWalletComponent,
    UserManCertWalletComponent,
    AdminUserAdvanceSearchDialog,
    AdminCertificationComponent,
    AdminCeuComponent,
    AdminAddNewTrainingDialog,
    AdminAddNewCertificationDialog,
    AdminUserDialog,
    AssessmentComponent,
    UserManAssessmentComponent,
    UserManAssessmentEditorComponent,
    MyAssessmentComponent,
    MyAssessmentViewComponent,
    AddProviderDialog,
    OfficeBoardComponent,
    AssociationListComponent,
    AssociationFormComponent,
    AssociationDetailComponent,
    AssociationEditComponent,
    AssociationActivityListComponent,
    AssociationActivityFormComponent,
    AssociationActivityDetailComponent,
    NewCertificationComponent,
    NewCertificationFormComponent,
    NewCertificationDetailComponent,
    NewCertDashboardComponent,
    CertificationConditionCheckComponent,
    TermDialog,
    TiptapComponent,
    NewCertificationEditComponent,
    NewCertificationDomainListComponent,
    NewCertificationDomainEditComponent,
    NewCertificationDomainViewComponent,
    NewCertificationDomainFormComponent,
    NewCertificationObjectiveListComponent,
    NewCertificationObjectiveEditComponent,
    NewCertificationObjectiveFormComponent,
    ViewObjectiveDialog,
    AdminAddUserCertificationModal,
    AdminUploadCeuCreditModal,
    AdminReportComponent,
    AdminSettingComponent,
    ProviderCertificationListComponent,
    ValidityCheckComponent,
    ValidityCheckLightComponent,
    UserManCeuDetailComponent,
    WalletCeuDetailComponent,
    EscapeHtmlPipe,
    StepperCeuComponent
  ],
  exports: [
    NewUserModal,
    UpcomingCeuAdvanceSearchDialog,
    PaginationComponent,
    PaginationDarkComponent,
    AdminUserAdvanceSearchDialog,
    TiptapComponent,
    ValidityCheckComponent,
    ValidityCheckLightComponent,
    EscapeHtmlPipe
  ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        LayoutModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        MaterialModule,
        NgxMaskModule.forRoot(maskConfig),
        JwtModule.forRoot({
            config: {
                tokenGetter,
                allowedDomains: [
                    'localhost:8000',
                    'staging-api.certwalletapp.com',
                    'production-api.certwalletapp.com',
                    'api.certwalletapp.com',
                ],
            },
        }),
        NgProgressModule.withConfig({
            thick: true,
            max: 150,
            meteor: true,
            color: '#FFB400',
        }),
        NgProgressModule,
        NgProgressHttpModule,
        MatTooltipModule,
        NgOptimizedImage,
    ],
  providers: [
      Auth,
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CustomHttpInterceptor,
        multi: true,
      },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {provide: MAT_DATE_LOCALE, useValue: 'en-EN'},
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {provide: MAT_DATE_FORMATS, useValue: PICKER_DATE_FORMATS},
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
